// src/pages/UserControlPage.tsx
//
// WS-E: Tenant-facing self-service profile page.
//
// Read-only for now — shows:
//   - Profile (from Auth0 claims): name, email, picture, roles
//   - Active tenant (from Supabase tenants row): slug, display name, time zone
//   - Effective features (tenant features + this user's overrides)
//   - Per-user feature overrides (if any)
//
// Editing the Auth0 profile (display name, picture) requires the Auth0
// Management API + a new Netlify function with an M2M token. Deferred —
// this MVP is read-only; users edit their Auth0 profile via the tenant
// admin or in the Auth0 dashboard.
//
// Visible from the landing page as the 'My Account' tile.

import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, RefreshCw, AlertCircle, User as UserIcon,
  Building2, Mail, Shield, Activity,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { adminApi } from '../services/adminApi';

interface UserControlPageProps {
  onBack?: () => void;
}

export default function UserControlPage({ onBack }: UserControlPageProps) {
  const { user, branding, claims, activeTenant } = useAuthContext();
  const slug = activeTenant?.slug ?? claims?.tenant_slug ?? null;

  // Tenant features + user overrides + effective features. All three queries
  // live on the agent's admin endpoints and route through the existing
  // Netlify admin-proxy.
  const featuresQ = useQuery({
    queryKey: ['account', 'features', slug],
    queryFn: () => adminApi.getAllFeatures(slug ?? undefined),
    enabled: !!slug,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const overridesQ = useQuery({
    queryKey: ['account', 'overrides', slug, user?.id],
    queryFn: () => adminApi.getAllUserOverrides(user!.id, slug ?? undefined),
    enabled: !!slug && !!user?.id,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const effectiveQ = useQuery({
    queryKey: ['account', 'effective', slug, user?.id],
    queryFn: () => adminApi.getEffectiveFeatures(user!.id, slug ?? undefined),
    enabled: !!slug && !!user?.id,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const refetchAll = () => {
    featuresQ.refetch();
    overridesQ.refetch();
    effectiveQ.refetch();
  };

  const overrideByKey = new Map((overridesQ.data ?? []).map((o) => [o.featureKey, o]));
  const effectiveSet = new Set(effectiveQ.data ?? []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1100px] mx-auto">
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center text-white shadow-lg shadow-sky-500/20">
                  <UserIcon size={18} />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-white tracking-tight">My Account</h1>
                  <p className="text-xs text-slate-500">
                    {branding?.company_name || 'GeniReports'} — profile &amp; permissions
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={refetchAll}
              disabled={featuresQ.isFetching || overridesQ.isFetching || effectiveQ.isFetching}
              className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={16} className={featuresQ.isFetching ? 'animate-spin' : ''} />
            </button>
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProfileCard />
          <TenantCard />
          <div className="lg:col-span-2">
            <FeaturesCard
              loading={featuresQ.isLoading || overridesQ.isLoading || effectiveQ.isLoading}
              error={featuresQ.error || overridesQ.error || effectiveQ.error}
              features={featuresQ.data ?? []}
              overrideByKey={overrideByKey}
              effectiveSet={effectiveSet}
              slug={slug}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

// ── Cards ─────────────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return <div className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-5">{children}</div>;
}

function CardHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-sky-300">{icon}</span>
      <h3 className="text-sm font-semibold text-white">{title}</h3>
    </div>
  );
}

function ProfileCard() {
  const { user, claims } = useAuthContext();
  return (
    <Card>
      <CardHeader icon={<UserIcon size={16} />} title="Profile" />
      <div className="flex items-start gap-4">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-14 h-14 rounded-full border border-slate-700" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400">
            <UserIcon size={24} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-base font-semibold text-white">
            {user ? `${user.firstName} ${user.lastName}`.trim() || user.name || '—' : '—'}
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-1">
            <Mail size={11} /> {user?.email || '—'}
          </div>
          <div className="text-[10px] text-slate-600 font-mono mt-1 break-all">{user?.id || ''}</div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-slate-700/40">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
          <Shield size={11} /> Roles
        </div>
        {(claims?.roles?.length ?? 0) === 0 ? (
          <div className="text-xs text-slate-500 italic">No roles assigned (you may be in dev mode).</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {claims!.roles!.map((r) => (
              <span
                key={r}
                className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-200 border border-violet-500/30"
              >
                {r}
              </span>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function TenantCard() {
  const { activeTenant, branding, claims } = useAuthContext();
  if (!activeTenant) {
    return (
      <Card>
        <CardHeader icon={<Building2 size={16} />} title="Active Tenant" />
        <div className="text-xs text-slate-500 italic">No active tenant selected.</div>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader icon={<Building2 size={16} />} title="Active Tenant" />
      <div className="text-base font-semibold text-white">{activeTenant.name}</div>
      <div className="text-xs text-slate-500 font-mono mt-1">{activeTenant.slug}</div>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Company</dt>
          <dd className="text-slate-200">{branding?.company_name ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Primary color</dt>
          <dd className="flex items-center gap-2 text-slate-200">
            {branding?.primary_color && (
              <span
                className="inline-block w-3 h-3 rounded-full border border-slate-600"
                style={{ background: branding.primary_color }}
              />
            )}
            <span className="font-mono text-xs">{branding?.primary_color ?? '—'}</span>
          </dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Agent URL</dt>
          <dd className="font-mono text-[11px] text-slate-300 break-all">{activeTenant.api_url ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Tenant ID</dt>
          <dd className="font-mono text-[11px] text-slate-300 break-all">{claims?.tenant_id ?? '—'}</dd>
        </div>
      </dl>
    </Card>
  );
}

function FeaturesCard({
  loading,
  error,
  features,
  overrideByKey,
  effectiveSet,
  slug,
}: {
  loading: boolean;
  error: unknown;
  features: { featureKey: string; isEnabled: boolean }[];
  overrideByKey: Map<string, { featureKey: string; isEnabled: boolean }>;
  effectiveSet: Set<string>;
  slug: string | null;
}) {
  return (
    <Card>
      <CardHeader icon={<Activity size={16} />} title="My Features" />

      {!slug && (
        <div className="text-sm text-slate-500 py-4">No active tenant — feature list unavailable.</div>
      )}

      {slug && loading && (
        <div className="text-sm text-slate-500 flex items-center gap-2 py-4">
          <RefreshCw size={14} className="animate-spin" /> Loading features...
        </div>
      )}

      {slug && error != null && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-4 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div>{error instanceof Error ? error.message : String(error)}</div>
        </div>
      )}

      {slug && !loading && !error && features.length === 0 && (
        <div className="text-sm text-slate-500 py-4">
          No tenant features defined yet.
        </div>
      )}

      {slug && !loading && !error && features.length > 0 && (
        <>
          <p className="text-xs text-slate-500 mb-4">
            <em>Effective</em> = what you actually have access to. <em>Tenant default</em> is your
            organization-wide setting; <em>Override</em> is a per-user grant set by your admin.
          </p>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-700/40">
              <tr>
                <th className="text-left py-2 font-medium">Feature</th>
                <th className="text-left py-2 font-medium w-32">Tenant default</th>
                <th className="text-left py-2 font-medium w-32">Your override</th>
                <th className="text-left py-2 font-medium w-32">Effective</th>
              </tr>
            </thead>
            <tbody>
              {features.map((f) => {
                const ov = overrideByKey.get(f.featureKey);
                const effective = effectiveSet.has(f.featureKey);
                return (
                  <tr key={f.featureKey} className="border-b border-slate-700/20">
                    <td className="py-2 font-mono text-xs text-slate-200">{f.featureKey}</td>
                    <td className="py-2"><Pill on={f.isEnabled} muted /></td>
                    <td className="py-2">
                      {ov
                        ? <Pill on={ov.isEnabled} label={ov.isEnabled ? 'FORCE ON' : 'FORCE OFF'} />
                        : <span className="text-[10px] text-slate-600 italic">— default —</span>}
                    </td>
                    <td className="py-2"><Pill on={effective} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

function Pill({ on, muted = false, label }: { on: boolean; muted?: boolean; label?: string }) {
  const text = label ?? (on ? 'ON' : 'OFF');
  if (on) {
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
        muted
          ? 'bg-emerald-500/10 text-emerald-300/80 border-emerald-500/20'
          : 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40'
      }`}>{text}</span>
    );
  }
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
      muted
        ? 'bg-slate-700/30 text-slate-400 border-slate-600/30'
        : 'bg-slate-700/60 text-slate-300 border-slate-600/40'
    }`}>{text}</span>
  );
}
